/**
 * one-stop/blocks-lists — `directory`, `checklist`, `my-visits`.
 *
 * directory  the prototype's "Browse all N departments" strip: one disclosure
 *            card, and inside it a grid of accent cards with the 3 px left rule
 *            in the department's own colour (two columns at 390 px).
 * checklist  the prototype's "This week" cards: title, detail, and
 *            the brand pill action on the right.
 * my-visits  the prototype's "Lost the text?" panel — the GUEST-LOOKUP variant.
 *            The list variant lands with the `visits` fragment; the session-bearing
 *            kind is declared but no fragment schema has shipped, so building the
 *            list now would mean inventing its shape.
 */
import { useState } from "react";
import type { JSX } from "react";
import { Button, Input } from "@v0y4ge/jrni-ui";
import { useOneStopActions } from "./actions";
import { configBool, configInt, configText } from "./config";
import { BlockHeading, Empty, Section, fragmentOf } from "./primitives";
import { accentFor } from "./theme";
import { plural, uiStrings } from "./strings";
import type { StringKey } from "./strings";
import type { BlockRenderProps } from "./types";

/* ── directory ────────────────────────────────────────────────── */

export function DirectoryBlock({ block, ctx }: BlockRenderProps): JSX.Element {
  const t = uiStrings(ctx.locale);
  const fragment = fragmentOf(block, "directory");
  // Registry default MIRRORED, not inverted (one-stop-builder kit §5).
  const showCapabilities = configBool(block.config, "showCapabilities", true);
  const collapsedByDefault = configBool(block.config, "collapsedByDefault", true);
  const [open, setOpen] = useState(!collapsedByDefault);

  const subtitle = configText(block.config, "subtitle", ctx.locale) ?? t("directory.subtitle");

  if (!fragment || fragment.nodes.length === 0) {
    const title = configText(block.config, "title", ctx.locale) ?? t("directory.title"); /* the catalog title when none is stored (CC 0546): stored pages rely on it */
    return (
      <Section state="empty" title={title} subtitle={subtitle}>
        <Empty>{t("directory.empty")}</Empty>
      </Section>
    );
  }

  const title =
    configText(block.config, "title", ctx.locale) ??
    t("directory.titleCount", { n: fragment.total });
  const more = Math.max(0, fragment.total - fragment.nodes.length);

  return (
    <Section>
      <div className="osr-disclosure jrni-disclosure">
        <button
          type="button"
          className="osr-disclosure-head"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
            {/* A heading may not sit inside a <button> (the disclosure trigger), so this
                title stays a span; the button is the block's named control. */}
            <span className="osr-panel-title">{title}</span>
            <span className="osr-row-detail jrni-meta">{subtitle}</span>
          </span>
          <span className="osr-disclosure-more">{open ? t("directory.hide") : t("directory.show")}</span>
        </button>
        {open ? (
          <div className="osr-disclosure-body">
            <div className="osr-grid-tight jrni-list">
              {fragment.nodes.map((node) => {
                // ui-config: a deterministic accent from the node's own id, so the
                // same department keeps the same colour without this renderer
                // knowing, or hardcoding, what any department is called.
                const accent = accentFor(node.nodeId);
                const caps = showCapabilities
                  ? (["walkIns", "books", "events"] as const)
                      .filter((k) => node.capabilities[k])
                      .map((k) => t(`directory.cap.${k}` as StringKey))
                  : [];
                const meta = [
                  node.childCount > 0 ? plural(t, "directory.inside", node.childCount) : null,
                  caps.length > 0 ? caps.join(" · ") : null,
                ]
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <div
                    key={node.nodeId}
                    className="osr-accent-card jrni-directory-node"
                    style={{ borderLeft: `3px solid ${accent}` }}
                  >
                    <span className="osr-accent-name jrni-directory-name">{node.name}</span>
                    {meta ? (
                      <span className="osr-accent-meta jrni-directory-capabilities" style={{ color: accent }}>
                        {meta}
                      </span>
                    ) : null}
                  </div>
                );
              })}
            </div>
            {fragment.hasMore && more > 0 ? (
              <p className="osr-overflow jrni-overflow">{t("directory.more", { n: more })}</p>
            ) : null}
          </div>
        ) : null}
      </div>
    </Section>
  );
}

/* ── checklist ───────────────────────────────────────────────── */

/**
 * Session-only: the resolver drops this block from the anonymous document, so
 * reaching this component at all means a session was involved. The fragment is
 * present-and-empty until the checklist resolver lands — render the empty state,
 * do not hide the section: "nothing for you this week" and "this section does not
 * exist" are different facts.
 */
export function ChecklistBlock({ block, ctx }: BlockRenderProps): JSX.Element {
  const t = uiStrings(ctx.locale);
  const fragment = fragmentOf(block, "checklist");
  const actions = useOneStopActions();
  const [expanded, setExpanded] = useState(false);

  // The registry default is `{"en-US":"Your checklist, {name}"}`. The name lives
  // on the identity fragment, not on this block — so an unresolved `{name}` falls
  // back to the chrome title rather than rendering a placeholder at a student.
  const authored = configText(block.config, "title", ctx.locale);
  // An unset or unfilled template falls back to the catalog title (CC 0546).
  const title = authored && !authored.includes("{name}") ? authored : t("checklist.title");
  const subtitle = configText(block.config, "subtitle", ctx.locale);
  const emptyText = configText(block.config, "emptyText", ctx.locale) ?? t("checklist.empty");
  const maxItems = configInt(block.config, "maxItems", 3) ?? 3;

  if (!fragment || fragment.cards.length === 0) {
    return (
      <Section state="empty" title={title} subtitle={subtitle}>
        <Empty>{emptyText}</Empty>
      </Section>
    );
  }

  const shown = expanded ? fragment.cards : fragment.cards.slice(0, maxItems);
  const hasMore = fragment.cards.length > shown.length || fragment.total > fragment.cards.length;
  const reason = actions.available ? null : (actions.unavailableReason || t("cta.pending.book"));

  return (
    <Section title={title} subtitle={subtitle}>
      <div className="osr-grid jrni-list">
        {shown.map((card) => {
          const label =
            card.ctaLabel ??
            (card.ctaTarget ? t(`checklist.cta.${card.ctaTarget.flow}` as StringKey) : null);
          return (
            <button
              key={card.cardId}
              type="button"
              className="osr-row jrni-row"
              disabled={reason !== null || card.ctaTarget === null}
              title={reason ?? undefined}
            >
              <span className="osr-row-main">
                <span
                  className="osr-row-title jrni-item-title"
                  style={
                    card.severity === "action-needed"
                      ? { color: "var(--jrni-color-status-warning-text)" }
                      : undefined
                  }
                >
                  {card.title}
                </span>
                {card.body ? <span className="osr-row-detail jrni-meta">{card.body}</span> : null}
              </span>
              {label ? <span className="osr-pill-action jrni-action">{label}</span> : null}
            </button>
          );
        })}
      </div>
      {reason ? <p className="osr-cta-reason jrni-cta-reason" style={{ marginTop: 10 }}>{reason}</p> : null}
      {/*
        `|| expanded` is the whole fix, and `hasMore` is why it is needed:
        it is recomputed AFTER expanding, as "more cards than shown OR more on the
        server than were sent". Once every delivered card is on screen both halves
        are false, so the control was removed from the DOM — and with it the only
        route back. "Show all 5" worked and then there was nothing to click, and
        `checklist.less` was a catalog string no visitor could reach unless the
        server ALSO held cards back beyond what it sent (CC 0020 finding 9).
        An expander stays mounted while it is expanded.
      */}
      {hasMore || expanded ? (
        <div style={{ marginTop: 10 }}>
          <Button variant="secondary" size="small" onClick={() => setExpanded((v) => !v)}>
            {expanded ? t("checklist.less") : t("checklist.more", { n: fragment.total })}
          </Button>
        </div>
      ) : null}
    </Section>
  );
}

/* ── my-visits ───────────────────────────────────────────────── */

/**
 * The guest-lookup variant, and only that. `my-visits` declares the session-bearing
 * kind `visits`, but NO fragment schema has shipped for it — so the list variant is
 * deliberately absent rather than invented. When the fragment lands, this component
 * gains a `fragmentOf(block, "visits")` branch above the lookup and nothing else
 * changes.
 *
 * The phone number is typed and held in this component only; it is submitted to the
 * lookup operation and never put in a URL.
 */
export function MyVisitsBlock({ block, ctx }: BlockRenderProps): JSX.Element {
  const t = uiStrings(ctx.locale);
  const actions = useOneStopActions();
  const [phone, setPhone] = useState("");

  const eyebrow = configText(block.config, "eyebrow", ctx.locale) ?? t("myVisits.eyebrow");
  const title = configText(block.config, "title", ctx.locale) ?? t("myVisits.title"); /* the catalog title when none is stored (CC 0546): stored pages rely on it */
  const body = configText(block.config, "body", ctx.locale) ?? t("myVisits.body");
  const fieldLabel = configText(block.config, "lookupLabel", ctx.locale) ?? t("myVisits.field");
  const submitLabel = configText(block.config, "submitLabel", ctx.locale) ?? t("myVisits.submit");
  /**
   * ⛔ UNAVAILABLE IN BOTH HOSTS, not only in the preview.
   *
   * `lookupVisits` is declared on `OneStopActions`, but there is no guest-lookup
   * OPERATION behind it: the live implementation throws `OneStopActionsUnavailable`
   * because the route does not exist, and the panel has no submit handler at all.
   * Deriving the reason from `actions.available` alone therefore left this the one
   * control on the live page that looked live and did nothing when pressed — the
   * same defect the signed-out panel's "Find my visit" link already avoided by
   * being unconditionally disabled. The reason is a VISIBLE line, not a `title`:
   * a tooltip does not exist on a touch device.
   *
   * This becomes `actions.available ? null : …` again the day the operation ships;
   * nothing else in this component changes.
   */
  const reason = actions.available
    ? t("cta.pending.lookup")
    : (actions.unavailableReason || t("cta.pending.lookup"));

  return (
    <Section>
      <div className="osr-panel" style={{ maxWidth: 520 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <span className="osr-panel-eyebrow jrni-eyebrow">{eyebrow}</span>
          {title ? <BlockHeading className="osr-panel-title jrni-block-heading">{title}</BlockHeading> : null}
        </div>
        <span className="osr-soft">{body}</span>
        <Input
          label={fieldLabel}
          type="tel"
          full
          value={phone}
          disabled={reason !== null}
          onChange={(event) => setPhone(event.target.value)}
        />
        <div className="osr-actions jrni-actions">
          <span className="osr-act jrni-action" style={{ flex: "1 1 0" }}>
            <Button hierarchy="secondary" full disabled={reason !== null}>
              {submitLabel}
            </Button>
          </span>
        </div>
        {reason ? <p className="osr-cta-reason jrni-cta-reason">{reason}</p> : null}
      </div>
    </Section>
  );
}
